import Form from '@rjsf/core';
import validator from '@rjsf/validator-ajv8';
import { useMemo, VFC } from 'react';

import { ParamsBaseProps } from 'dashboard/components/WorkflowControlPanel/StepConfigAction/ParamsBaseProps';
import { BaseInputTemplate } from 'dashboard/components/WorkflowControlPanel/StepConfigAction/rjsf/BaseInputTemplate';
import { FieldTemplate } from 'dashboard/components/WorkflowControlPanel/StepConfigAction/rjsf/FieldTemplate';
import { HandlebarsField } from 'dashboard/components/WorkflowControlPanel/StepConfigAction/rjsf/HandlebarsField';
import { HtmlEditorWithPreviewWidget } from 'dashboard/components/WorkflowControlPanel/StepConfigAction/rjsf/HtmlEditorWithPreviewWidget';
import * as ObjectFieldTemplate from 'dashboard/components/WorkflowControlPanel/StepConfigAction/rjsf/ObjectFieldTemplate';
import { PDSCheckBoxField } from 'dashboard/components/WorkflowControlPanel/StepConfigAction/rjsf/PDSCheckBoxField';
import { SelectWidget } from 'dashboard/components/WorkflowControlPanel/StepConfigAction/rjsf/SelectWidget';
import { generateRjsfSchemaAndErrors } from 'dashboard/components/WorkflowControlPanel/StepConfigAction/rjsf/utils';
import { UsableObjectConfig } from 'dashboard/lib/workflows';
import { TemplateFilter } from 'dashboard/lib/workflows/objects';
import {
  WorkflowActionMeta,
  WorkflowConfigError,
  WorkflowError,
  WorkflowErrorType,
} from 'dashboard/models/workflow';
import { GenericWorkflowAction } from 'dashboard/models/workflow-schema';
import { WorkflowActionFromConfig } from 'dashboard/models/workflow-version';
import Spacer from 'design-system/components/Spacer';
import { JsonSchema } from 'lib/json-schema';

interface RjsfActionBuilderProps extends ParamsBaseProps {
  workflowAction: GenericWorkflowAction;
  actionParameters: WorkflowActionFromConfig['params'];
  editable: boolean;
  usableObjects: UsableObjectConfig[];
  jsonSchema: JsonSchema;
  tokensToDisplayValue: Record<string, string>;
  actionMeta: WorkflowActionMeta;
  errors: WorkflowError[];
  updateActionParams: (
    params: WorkflowActionFromConfig['params'],
    template?: TemplateFilter,
  ) => void;
}

const ID_SEPARATOR = '__';

const RjsfActionBuilder: VFC<RjsfActionBuilderProps> = ({
  workflowAction,
  actionParameters,
  editable,
  step,
  usableObjects,
  jsonSchema,
  tokensToDisplayValue,
  actionMeta,
  errors,
  updateActionParams,
}) => {
  const errorsForStep = useMemo(
    () =>
      errors.filter(
        (error): error is WorkflowConfigError =>
          (error.type === WorkflowErrorType.Publish || error.type === WorkflowErrorType.Save) &&
          error.token === step.token,
      ),
    [errors, step.token],
  );

  const { schema: generatedSchema, errorSchema: extraErrors } = useMemo(() => {
    return generateRjsfSchemaAndErrors({
      schema: workflowAction.request.schema,
      uiSchema: workflowAction.request.uiSchema,
      usableObjects,
      actionParameters,
      actionMeta,
    });
  }, [actionMeta, actionParameters, usableObjects, workflowAction]);

  return (
    <>
      <Spacer height={2} />
      <Form
        schema={generatedSchema}
        onChange={(data, id) => {
          const property = id?.split(ID_SEPARATOR).pop();
          const template =
            property && data.uiSchema[property]?.['ui:options']?.['templateObjectType']
              ? {
                  type: data.uiSchema[property]['ui:options']['templateObjectType'],
                  id: data.formData[property],
                }
              : undefined;
          updateActionParams(data.formData, template);
        }}
        uiSchema={{
          ...workflowAction.request.uiSchema,
          'ui:submitButtonOptions': { norender: true },
        }}
        validator={validator}
        formContext={{ jsonSchema, tokensToDisplayValue, usableObjects, errorsForStep }}
        formData={actionParameters}
        idPrefix={`params-${workflowAction.key}`}
        idSeparator={ID_SEPARATOR}
        className={workflowAction.key}
        id={`params-${workflowAction.key}`}
        key={step.token}
        fields={{
          handlebars: HandlebarsField,
          checkbox: PDSCheckBoxField,
        }}
        widgets={{
          SelectWidget,
          'html-editor-with-preview': HtmlEditorWithPreviewWidget,
        }}
        templates={{
          BaseInputTemplate,
          FieldTemplate,
          ObjectFieldTemplate: ObjectFieldTemplate.ObjectFieldTemplate,
        }}
        showErrorList={false}
        extraErrors={extraErrors}
        disabled={!editable}
        experimental_defaultFormStateBehavior={{ arrayMinItems: { populate: 'never' } }}
        liveOmit
        omitExtraData
      />
    </>
  );
};

export default RjsfActionBuilder;
